import { useState } from "react";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/format";

interface MonthlyPoint {
  month: string;
  earnings: number;
  sessions: number;
}

interface Props {
  data: MonthlyPoint[];
  monthNames: string[];
  emptyLabel: string;
}

const MONTH_RE = /^(\d{4})-(\d{2})$/;

export function MonthlyBarChart({ data, monthNames, emptyLabel }: Props) {
  const [hover, setHover] = useState<number | null>(null);

  if (data.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">{emptyLabel}</p>;
  }

  const max = Math.max(...data.map((d) => d.earnings), 1);
  const width = 720;
  const height = 260;
  const padTop = 28;
  const padBottom = 54;
  const padX = 24;
  const chartWidth = width - padX * 2;
  const chartHeight = height - padTop - padBottom;
  const step = chartWidth / Math.max(data.length, 1);
  const barWidth = Math.min(48, step * 0.52);

  const labelForMonth = (m: string) => {
    const match = MONTH_RE.exec(m);
    if (!match || !match[2]) return m;
    const idx = parseInt(match[2], 10) - 1;
    return (monthNames[idx] ?? m).slice(0, 3);
  };

  const yTicks = 3;
  const first = data[0]!;
  const peak = data.reduce((best, d) => (d.earnings > best.earnings ? d : best), first);
  const peakIdx = data.findIndex((d) => d.month === peak.month);

  return (
    <div>
      <div className="mb-1 flex flex-wrap items-baseline justify-end gap-2">
        <span className="text-xs text-muted-foreground">Vrh</span>
        <span
          className="text-sm font-semibold text-foreground tabular-nums"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {labelForMonth(peak.month)} · {formatCurrency(peak.earnings)}
        </span>
      </div>

      <div>
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full" role="img">
          <defs>
            <linearGradient id="barGrad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="oklch(37.12% 0.059 186.62)" />
              <stop offset="100%" stopColor="oklch(65% 0.15 84)" />
            </linearGradient>
            <linearGradient id="barGradHover" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="oklch(49.83% 0.0663 186.62)" />
              <stop offset="100%" stopColor="oklch(55% 0.17 84)" />
            </linearGradient>
          </defs>

          {Array.from({ length: yTicks + 1 }, (_, i) => {
            const y = padTop + (chartHeight / yTicks) * i;
            const value = max * (1 - i / yTicks);
            return (
              <g key={i}>
                <line
                  x1={padX}
                  x2={width - padX}
                  y1={y}
                  y2={y}
                  stroke="oklch(90% 0.02 84)"
                  strokeDasharray={i === yTicks ? undefined : "2 5"}
                />
                {i < yTicks && (
                  <text
                    x={padX}
                    y={y - 4}
                    className="fill-muted-foreground/60"
                    fontSize={9}
                    fontWeight={500}
                    letterSpacing={1.4}
                    style={{ textTransform: "uppercase", fontVariantNumeric: "tabular-nums" }}
                  >
                    {Math.round(value)}
                  </text>
                )}
              </g>
            );
          })}

          {data.map((d, i) => {
            const h = (d.earnings / max) * chartHeight;
            const cx = padX + step * i + step / 2;
            const x = cx - barWidth / 2;
            const y = padTop + chartHeight - h;
            const isHover = hover === i;
            const isPeak = i === peakIdx;

            return (
              <g
                key={d.month}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              >
                <rect
                  x={cx - step / 2}
                  y={padTop}
                  width={step}
                  height={chartHeight}
                  fill="transparent"
                />
                {h > 0 && (
                  <rect
                    x={x}
                    y={y}
                    width={barWidth}
                    height={h}
                    rx={2}
                    fill={isHover ? "url(#barGradHover)" : "url(#barGrad)"}
                    className={cn("transition-opacity", !isHover && hover !== null && "opacity-60")}
                  />
                )}
                {isPeak && !isHover && h > 0 && (
                  <circle cx={cx} cy={y - 8} r={2} fill="oklch(55% 0.15 84)" />
                )}
                {isHover && (
                  <>
                    <line
                      x1={cx}
                      x2={cx}
                      y1={padTop}
                      y2={padTop + chartHeight}
                      stroke="oklch(37.12% 0.059 186.62)"
                      strokeDasharray="2 3"
                      strokeOpacity={0.5}
                    />
                    <text
                      x={cx}
                      y={y - 10}
                      textAnchor="middle"
                      className="fill-foreground"
                      fontSize={12}
                      fontWeight={600}
                      style={{ fontVariantNumeric: "tabular-nums", fontFamily: "var(--font-heading)" }}
                    >
                      {formatCurrency(d.earnings)}
                    </text>
                  </>
                )}
                <text
                  x={cx}
                  y={padTop + chartHeight + 20}
                  textAnchor="middle"
                  className="fill-muted-foreground"
                  fontSize={10}
                  fontWeight={600}
                  letterSpacing={1.8}
                  style={{ textTransform: "uppercase", fontFamily: "var(--font-heading)" }}
                >
                  {labelForMonth(d.month)}
                </text>
                <text
                  x={cx}
                  y={padTop + chartHeight + 36}
                  textAnchor="middle"
                  className="fill-muted-foreground/55"
                  fontSize={9}
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {d.sessions}×
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
